/**
 * AssetPreloadBar - Barra global de progreso de precarga
 *
 * Se muestra automáticamente cuando hay assets precargándose.
 * Usa Asset.subscribe() para escuchar cambios.
 *
 * UX: Muestra "Iniciando carga" 300ms al inicio y "Carga completa" 300ms al final
 */

import { useState, useEffect, useRef } from 'react';
import { Asset } from '@rts';
import './AssetPreloadBar.css';

const DELAY_MS = 300;

export function AssetPreloadBar() {
  const [visible, setVisible] = useState(false);
  const [displayState, setDisplayState] = useState({
    progress: 0,
    label: '',
    phase: 'idle', // 'idle' | 'starting' | 'loading' | 'finishing'
  });
  const timeoutRef = useRef(null);

  useEffect(() => {
    const handleStateChange = (newState) => {
      // Limpiar timeout anterior
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      if (newState.isPreloading && !visible) {
        // Inicio de precarga: mostrar "Iniciando carga"
        setVisible(true);
        setDisplayState({
          progress: 0,
          label: 'Iniciando carga...',
          phase: 'starting',
        });

        timeoutRef.current = setTimeout(() => {
          setDisplayState({
            progress: newState.progress,
            label: newState.label,
            phase: 'loading',
          });
        }, DELAY_MS);
      } else if (newState.isPreloading && visible) {
        // Durante precarga: actualizar progreso
        setDisplayState((prev) => ({
          progress: newState.progress,
          label: newState.label,
          phase: prev.phase === 'starting' ? 'starting' : 'loading',
        }));
      } else if (!newState.isPreloading && visible) {
        // Fin de precarga: mostrar "Carga completa"
        setDisplayState({
          progress: 100,
          label: 'Carga completa',
          phase: 'finishing',
        });

        timeoutRef.current = setTimeout(() => {
          setVisible(false);
          setDisplayState({ progress: 0, label: '', phase: 'idle' });
        }, DELAY_MS);
      }
    };

    // Obtener estado inicial
    handleStateChange(Asset.getState());

    // Suscribirse a cambios
    const unsubscribe = Asset.subscribe(handleStateChange);

    return () => {
      unsubscribe();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [visible]);

  if (!visible) {
    return null;
  }

  return (
    <div className="asset-preload-bar">
      <div className="asset-preload-bar__content">
        <div className="asset-preload-bar__label">{displayState.label}</div>
        <div className="asset-preload-bar__track">
          <div
            className="asset-preload-bar__fill"
            style={{ width: `${displayState.progress}%` }}
          />
        </div>
        <div className="asset-preload-bar__percentage">{displayState.progress}%</div>
      </div>
    </div>
  );
}

export default AssetPreloadBar;
